import { TextField, ToggleButton, ToggleButtonGroup } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { merge } from 'lodash';
import { GaugeChartSpec, SortOption, ThresholdOptions, UNIT_SETTINGS_NAME } from '@/core';
import {
  OptionsEditorColumn,
  OptionsEditorControl,
  OptionsEditorGrid,
  OptionsEditorGroup,
  SORT_OPTIONS,
  SortSelector,
  SortSelectorProps,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import { GaugeChartThresholdsEditor } from './GaugeChartThresholdsEditor';
import {
  DEFAULT_MAX_PERCENT,
  DEFAULT_MAX_PERCENT_DECIMAL,
  DEFAULT_MODE,
  DEFAULT_UNIT,
  GaugeChartOptionsEditorProps,
} from './gauge-chart-model';

export function GaugeChartOptionsEditorSettings(props: GaugeChartOptionsEditorProps) {
  const { onChange, value } = props;

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit) => {
    onChange(
      produce(value, (draft: GaugeChartSpec) => {
        draft.unit = newUnit;
      })
    );
  };

  // ensures decimal_places defaults to correct value
  const unit = merge({}, DEFAULT_UNIT, value.unit);

  // max only needs to be set explicitly for units other than Percent and PercentDecimal
  let maxPlaceholder = 'Auto';
  if (unit.kind === 'Percent') {
    maxPlaceholder = DEFAULT_MAX_PERCENT.toString();
  } else if (unit.kind === 'PercentDecimal') {
    maxPlaceholder = DEFAULT_MAX_PERCENT_DECIMAL.toString();
  }

  const handleThresholdsChange = (thresholds: ThresholdOptions) => {
    onChange(
      produce(value, (draft: GaugeChartSpec) => {
        draft.thresholds = thresholds;
      })
    );
  };

  const handleModeChange = (_: unknown, option: GaugeChartSpec['mode']) => {
    onChange(
      produce(value, (draft: GaugeChartSpec) => {
        // Toggle button group allows you to select none, in which case the option will be null
        // Prevent any toggling when that happens
        if (option !== null) {
          draft.mode = option;
        }

        // The default functionality for this property is "gauge", so if the value is undefined or gauge,
        // we can just delete this key from the object
        if (draft.mode === undefined || draft.mode === DEFAULT_MODE) {
          delete draft.mode;
        }
      })
    );
  };

  const handleSortChange: SortSelectorProps<SortOption>['onChange'] = (newSort?: SortOption) => {
    onChange(
      produce(value, (draft: GaugeChartSpec) => {
        draft.sort = newSort;
      })
    );
  };

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Visual">
          <OptionsEditorControl
            label="Mode"
            control={
              <ToggleButtonGroup value={value.mode ?? DEFAULT_MODE} onChange={handleModeChange}>
                <ToggleButton value="Gauge" aria-label="display gauge series">
                  Gauge
                </ToggleButton>
                <ToggleButton value="Bar" aria-label="display bar series">
                  Bar
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />

          <SortSelector value={value.sort} onChange={handleSortChange} options={SORT_OPTIONS} />
        </OptionsEditorGroup>
        <OptionsEditorGroup title="Misc">
          <OptionsEditorControl
            label="Max"
            control={
              <TextField
                type="number"
                value={value.max ?? ''}
                onChange={(e) => {
                  // ensure empty value resets to undef to allow chart to calculate max
                  const newValue = e.target.value ? Number(e.target.value) : undefined;
                  onChange(
                    produce(value, (draft: GaugeChartSpec) => {
                      draft.max = newValue;
                    })
                  );
                }}
                placeholder={maxPlaceholder}
              />
            }
          />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector value={unit} onChange={handleUnitChange} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <GaugeChartThresholdsEditor thresholds={value.thresholds} onChange={handleThresholdsChange} />
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
